// Demo-session placement registry, backing the mock myBidsApi overlay. The
// bid store's mock branch records every successful placement here; readers
// must never trust sessionStorage's contents without validating shape —
// it's plain JSON a user session could edit or another tab could leave
// truncated. Exactly one of itemId/bundleId is non-null per placement
// (item and bundle ids are independent PK spaces). sessionStorage so a new
// tab starts clean.

export interface MockPlacement {
  itemId: number | null;
  bundleId: number | null;
  title: string;
  amountCents: number;
  maxAmountCents: number | null;
}

const KEY = 'neon-auctions:mock-placed-bids';

function sameEntity(a: MockPlacement, b: MockPlacement): boolean {
  return a.itemId != null ? a.itemId === b.itemId : a.bundleId != null && a.bundleId === b.bundleId;
}

function isValidPlacement(value: unknown): value is MockPlacement {
  if (typeof value !== 'object' || value === null) return false;
  const p = value as Record<string, unknown>;
  const itemIdValid = p.itemId === null || typeof p.itemId === 'number';
  const bundleIdValid = p.bundleId === null || typeof p.bundleId === 'number';
  const exactlyOneId = (typeof p.itemId === 'number') !== (typeof p.bundleId === 'number');
  return (
    itemIdValid &&
    bundleIdValid &&
    exactlyOneId &&
    typeof p.title === 'string' &&
    typeof p.amountCents === 'number' &&
    (p.maxAmountCents === null || typeof p.maxAmountCents === 'number')
  );
}

export function readMockPlacements(): MockPlacement[] {
  if (typeof sessionStorage === 'undefined') return [];
  try {
    const raw = sessionStorage.getItem(KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(isValidPlacement);
  } catch {
    return [];
  }
}

export function recordMockPlacement(placement: MockPlacement): void {
  if (typeof sessionStorage === 'undefined') return;
  try {
    const rest = readMockPlacements().filter(p => !sameEntity(p, placement));
    sessionStorage.setItem(KEY, JSON.stringify([...rest, placement]));
  } catch {
    // quota / private mode — demo state simply won't persist
  }
}
